import React, { useEffect, useCallback } from 'react';
import { useWidgetStore } from '../hooks';
import { useWidgetConfig } from '../context';
import { QuestionIcon } from '../icons';

/**
 * Circular "?" help button rendered at the bottom-right (or left) of the viewport.
 * Shows a badge with the count of unseen release notes.
 */
export function SupportWidgetTrigger() {
  const config = useWidgetConfig();
  const toggle = useWidgetStore((s) => s.toggle);
  const isOpen = useWidgetStore((s) => s.isOpen);
  const seenReleases = useWidgetStore((s) => s.seenReleases);

  const unseenCount = (config.releases ?? []).filter(
    (r) => !seenReleases.has(r.id),
  ).length;

  const hotkey = config.hotkey ?? '?';
  const position = config.triggerPosition ?? 'bottom-right';

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Skip if the user is typing in an input / textarea / contenteditable
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement).isContentEditable) return;

      if (e.key === hotkey) {
        e.preventDefault();
        toggle();
      }
      if (e.key === 'Escape' && isOpen) {
        toggle();
      }
    },
    [hotkey, toggle, isOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const positionStyles: React.CSSProperties =
    position === 'bottom-left'
      ? { left: '20px', right: 'auto' }
      : { right: '20px', left: 'auto' };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Toggle support panel"
      aria-expanded={isOpen}
      style={{
        position: 'fixed',
        bottom: '20px',
        ...positionStyles,
        width: '48px',
        height: '48px',
        minWidth: '48px',
        minHeight: '48px',
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--sw-accent-primary, #4cc9f0)',
        color: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        zIndex: config.zIndex ?? 50,
        padding: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
      }}
    >
      <QuestionIcon size={24} />
      {unseenCount > 0 && (
        <span
          aria-label={`${unseenCount} unseen updates`}
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            minWidth: '20px',
            height: '20px',
            borderRadius: '10px',
            background: '#ef4444',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 5px',
            lineHeight: 1,
          }}
        >
          {unseenCount > 99 ? '99+' : unseenCount}
        </span>
      )}
    </button>
  );
}
